import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { X, ArrowRight, Share2, Clock, Camera, Loader2, Users, Sparkles, Swords, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { getComparison } from "@/lib/api";
import { STATUS_META, NARRATOR_INTROS, pick } from "@/lib/copy";
import { rarityFor, personalityFor } from "@/lib/fun";
import { haptic } from "@/lib/haptics";
import { play } from "@/lib/sound";
import SoundToggle from "@/components/SoundToggle";
import ShareCardModal from "@/components/ShareCardModal";
import AccusationModal from "@/components/AccusationModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function confidenceLabel(c) {
  if (c >= 80) return "Dead certain";
  if (c >= 60) return "Fairly sure";
  if (c >= 40) return "A hunch";
  return "Wild guess";
}

// Fridge-door-opens intro
function DoorIntro({ onDone }) {
  useEffect(() => {
    play("click");
    const t1 = setTimeout(() => play("whoosh"), 260);
    const t2 = setTimeout(onDone, 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onDone]);

  const doorStyle = "absolute top-0 h-full w-1/2 bg-gradient-to-b from-[#e9f1f2] to-[#c9d7da]";
  return (
    <motion.div className="absolute inset-0 z-40 overflow-hidden" exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      <div className="absolute inset-0 bg-gradient-to-b from-[#fff6e0] to-[#eef5f6]" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.p
          className="font-serif text-2xl text-[#4a5c62]"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.4 }}
        >
          Opening the fridge…
        </motion.p>
      </div>
      <motion.div
        className={`${doorStyle} left-0 origin-left border-r border-white/70`}
        style={{ boxShadow: "inset -24px 0 44px -22px rgba(20,40,45,0.5)" }}
        initial={{ rotateY: 0 }}
        animate={{ rotateY: -108 }}
        transition={{ delay: 0.26, duration: 1.05, ease: [0.7, 0, 0.3, 1] }}
      >
        <span className="absolute right-3 top-1/2 -translate-y-1/2 w-1.5 h-16 rounded-full bg-white/70" />
      </motion.div>
      <motion.div
        className={`${doorStyle} right-0 origin-right border-l border-white/70`}
        style={{ boxShadow: "inset 24px 0 44px -22px rgba(20,40,45,0.5)" }}
        initial={{ rotateY: 0 }}
        animate={{ rotateY: 108 }}
        transition={{ delay: 0.26, duration: 1.05, ease: [0.7, 0, 0.3, 1] }}
      >
        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-1.5 h-16 rounded-full bg-white/70" />
      </motion.div>
    </motion.div>
  );
}

export default function Reveal() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [comp, setComp] = useState(location.state || null);
  const [loading, setLoading] = useState(!location.state);
  const [step, setStep] = useState(0);
  const [door, setDoor] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showAccuse, setShowAccuse] = useState(false);

  useEffect(() => {
    if (comp) return;
    getComparison(id)
      .then(setComp)
      .catch(() => setComp(false))
      .finally(() => setLoading(false));
  }, [id, comp]);

  const result = comp && comp.result;
  const narrator = useMemo(() => pick(NARRATOR_INTROS), []);

  const cards = useMemo(() => {
    if (!result) return [];
    const list = [{ type: "intro" }];
    const items = result.items || [];
    if (items.length === 0) list.push({ type: "vibe" });
    else items.forEach((it) => list.push({ type: "item", item: it }));
    list.push({ type: "summary" });
    return list;
  }, [result]);

  if (loading) {
    return (
      <div className="app-shell min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 spin-slow text-[#4a5c62]" />
      </div>
    );
  }

  if (!comp || !result) {
    return (
      <div className="app-shell min-h-[100dvh] flex flex-col items-center justify-center px-8 text-center gap-4">
        <p className="font-serif text-3xl">This case file has gone cold.</p>
        <button onClick={() => navigate("/")} className="btn-ink rounded-sm px-6 py-3">Back to the fridge</button>
      </div>
    );
  }

  if (result.readable === false) {
    return (
      <div className="app-shell min-h-[100dvh] flex flex-col items-center justify-center px-8 text-center gap-5" data-testid="reshoot-screen">
        <Camera className="w-10 h-10 text-[#4a5c62]" strokeWidth={1.2} />
        <p className="font-serif text-3xl leading-tight">
          {result.reshoot_reason || "We couldn't read the fridge. Too dark, too blurry, or too mysterious."}
        </p>
        <button data-testid="reshoot-btn" onClick={() => navigate("/capture?role=before")} className="btn-ink rounded-sm px-6 py-4 flex items-center gap-2">
          Take it again <Camera className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  const isLast = step === cards.length - 1;
  const card = cards[step];
  const progress = ((step + 1) / cards.length) * 100;

  const advance = () => {
    if (!isLast) {
      haptic(8);
      play("flip");
      setStep((s) => s + 1);
    }
  };

  return (
    <div className="app-shell min-h-[100dvh] flex flex-col relative" data-testid="reveal-screen" style={{ perspective: 1400 }}>
      <AnimatePresence>{door && <DoorIntro onDone={() => setDoor(false)} />}</AnimatePresence>

      <div className="sticky top-0 z-20 bg-[#eef5f6]/85 backdrop-blur px-6 pt-[calc(env(safe-area-inset-top)+1.25rem)] pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <SoundToggle />
            <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">The reveal</span>
          </div>
          <button data-testid="reveal-close-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="h-[3px] bg-[rgba(120,150,158,0.3)] rounded-full overflow-hidden">
          <motion.div className="h-full bg-[#16242a]" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <div className="flex-1 flex flex-col px-6" onClick={advance} data-testid="reveal-card-area" style={{ perspective: 1200 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="flex-1 flex flex-col justify-center py-10 min-h-[70dvh]"
            style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
            initial={{ opacity: 0, rotateY: -70, y: 10 }}
            animate={{ opacity: 1, rotateY: 0, y: 0 }}
            exit={{ opacity: 0, rotateY: 70, y: -10 }}
            transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
          >
            {card.type === "intro" && <IntroCard comp={comp} result={result} narrator={narrator} />}
            {card.type === "vibe" && <VibeCard result={result} />}
            {card.type === "item" && <ItemCard item={card.item} />}
            {card.type === "summary" && (
              <SummaryCard
                result={result}
                fridgeCode={comp.fridge_code}
                onFridge={() => navigate(`/fridge/${comp.fridge_code}`)}
                onShare={() => setShowShare(true)}
                onAccuse={() => setShowAccuse(true)}
                onCook={() => navigate("/cook")}
                onTimeline={() => navigate("/timeline")}
                onNew={() => navigate("/")}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {!isLast && (
        <div className="pb-[calc(env(safe-area-inset-bottom)+2rem)] px-6">
          <button
            data-testid="reveal-next-btn"
            onClick={(e) => {
              e.stopPropagation();
              advance();
            }}
            className="w-full btn-ink rounded-sm py-4 flex items-center justify-center gap-2"
          >
            Next <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      )}

      <AnimatePresence>
        {showShare && <ShareCardModal result={result} onClose={() => setShowShare(false)} />}
        {showAccuse && <AccusationModal result={result} onClose={() => setShowAccuse(false)} />}
      </AnimatePresence>
    </div>
  );
}

function IntroCard({ comp, result, narrator }) {
  return (
    <div className="text-center">
      <div className="flex justify-center gap-3 mb-8">
        <Polaroid label="Before" url={`${BACKEND_URL}${comp.before_url}`} />
        <Polaroid label="After" url={`${BACKEND_URL}${comp.after_url}`} />
      </div>
      <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">Confidence: {confidenceLabel(result.overall_confidence || 0)}</p>
      <h1 className="font-serif text-5xl sm:text-6xl font-black tracking-tighter mt-4 leading-[0.95]">The verdict is in.</h1>
      <p className="font-serif text-lg italic text-[#4a5c62] mt-4">{narrator}</p>
      <p className="text-sm text-[#4a5c62] mt-2">Tap to reveal, one item at a time.</p>
    </div>
  );
}

function Polaroid({ label, url }) {
  return (
    <div className="glass rounded-sm p-2 shadow-[0_8px_32px_rgba(20,40,45,0.1)] -rotate-2 first:rotate-2">
      <img src={url} alt={label} className="w-24 h-32 object-cover rounded-sm bg-[#e9e5db]" />
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#4a5c62] text-center mt-1.5">{label}</p>
    </div>
  );
}

function ItemCard({ item }) {
  const meta = STATUS_META[item.status] || STATUS_META.still_here;
  const conf = item.confidence || 0;
  const rarity = rarityFor(item);
  return (
    <div className="text-center" data-testid="reveal-item-card">
      <div className="flex items-center justify-center gap-2 mb-7 flex-wrap">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${meta.color}22` }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
          <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: meta.color }}>{meta.label}</span>
        </div>
        <motion.div
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
          style={{ borderColor: meta.color, color: meta.color }}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.55, type: "spring", stiffness: 300, damping: 15 }}
          data-testid="rarity-badge"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.6} />
          <span className="text-xs font-semibold tracking-wide">{rarity.label}</span>
        </motion.div>
      </div>
      <motion.h2
        className="font-serif text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95]"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        {item.name}
      </motion.h2>
      <motion.p
        className="font-serif text-2xl italic text-[#16242a] mt-6 max-w-[20rem] mx-auto leading-snug"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
      >
        {item.copy}
      </motion.p>
      <div className="max-w-[16rem] mx-auto mt-10">
        <div className="flex items-center justify-between text-xs text-[#4a5c62] mb-2">
          <span className="uppercase tracking-[0.2em] font-semibold">{confidenceLabel(conf)}</span>
          <span className="tabular">{conf}%</span>
        </div>
        <div className="h-[3px] bg-[rgba(120,150,158,0.3)] rounded-full overflow-hidden">
          <motion.div className="h-full" style={{ backgroundColor: meta.color }} initial={{ width: 0 }} animate={{ width: `${conf}%` }} transition={{ delay: 0.5, duration: 0.7 }} />
        </div>
      </div>
    </div>
  );
}

function VibeCard({ result }) {
  return (
    <div className="text-center" data-testid="reveal-vibe-card">
      <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62] mb-6">Inconclusive</p>
      <h2 className="font-serif text-4xl sm:text-5xl font-black tracking-tight leading-tight">
        {result.vibe_copy || "Suspiciously little activity here."}
      </h2>
    </div>
  );
}

function SummaryCard({ result, fridgeCode, onFridge, onShare, onAccuse, onCook, onTimeline, onNew }) {
  const personality = useMemo(() => personalityFor(result), [result]);

  useEffect(() => {
    play("tada");
    haptic([10, 40, 10, 40, 20]);
    const base = { origin: { y: 0.35 }, disableForReducedMotion: true };
    confetti({ ...base, particleCount: 90, spread: 70, scalar: 0.9, colors: ["#d0583f", "#5f9e79", "#d59a45", "#16242a"] });
    // steam puff
    setTimeout(() => confetti({ ...base, particleCount: 40, spread: 120, startVelocity: 22, gravity: 0.4, scalar: 1.4, ticks: 120, colors: ["#ffffff", "#e6eef0", "#dbe6e9"] }), 180);
    return () => {
      try {
        confetti.reset();
      } catch {
        /* noop */
      }
    };
  }, []);

  const challenge = async () => {
    haptic(12);
    const url = window.location.origin;
    const text = `My fridge is "${personality.title}" — ${personality.blurb} Think yours is worse? ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "What's in the Fridge", text, url });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* noop */
    }
    toast("Challenge copied — send it to a flatmate.");
  };

  return (
    <div className="text-center" data-testid="reveal-summary-card">
      <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">The tally</p>
      <h2 className="font-serif text-5xl font-black tracking-tighter mt-4 leading-[0.95]">{result.summary_stat || "Case closed."}</h2>
      <p className="font-serif text-xl italic text-[#4a5c62] mt-5 max-w-[20rem] mx-auto leading-snug">{result.summary_copy}</p>

      {/* Fridge personality */}
      <motion.div
        className="glass rounded-lg px-5 py-5 mt-8 text-center"
        initial={{ opacity: 0, y: 14, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.25, type: "spring", stiffness: 240, damping: 18 }}
        data-testid="fridge-personality"
      >
        <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">Your fridge personality</p>
        <p className="font-serif text-4xl font-black tracking-tight mt-2 leading-none">{personality.title}</p>
        <p className="text-sm text-[#4a5c62] mt-2">{personality.blurb}</p>
      </motion.div>

      {result.longest_resident && (
        <div className="glass rounded-sm px-5 py-4 mt-4 text-left">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62]">Longest resident</p>
          <p className="font-serif text-2xl mt-1">{result.longest_resident.name}</p>
          {result.longest_resident.copy && <p className="text-sm text-[#4a5c62] mt-1">{result.longest_resident.copy}</p>}
        </div>
      )}

      <div className="mt-10 flex flex-col gap-3">
        <button data-testid="open-share-btn" onClick={onShare} className="btn-ink rounded-sm py-4 flex items-center justify-center gap-2">
          <Share2 className="w-5 h-5" strokeWidth={1.5} /> Make a share card
        </button>
        <button data-testid="challenge-btn" onClick={challenge} className="rounded-sm py-4 font-medium flex items-center justify-center gap-2 text-[#eef5f6]" style={{ background: "linear-gradient(135deg,#d0583f,#d59a45)" }}>
          <Swords className="w-5 h-5" strokeWidth={1.5} /> Challenge a flatmate
        </button>
        <button data-testid="open-accuse-btn" onClick={onAccuse} className="glass rounded-sm py-4 font-medium hover:bg-white/50 transition-colors">
          Start an accusation
        </button>
        <button data-testid="reveal-cook-btn" onClick={onCook} className="glass rounded-sm py-4 font-medium hover:bg-white/50 transition-colors flex items-center justify-center gap-2">
          <ChefHat className="w-4 h-4" strokeWidth={1.5} /> What can I cook?
        </button>
        {fridgeCode && (
          <button data-testid="summary-fridge-btn" onClick={onFridge} className="glass rounded-sm py-4 font-medium hover:bg-white/50 transition-colors flex items-center justify-center gap-2">
            <Users className="w-4 h-4" strokeWidth={1.5} /> Back to shared fridge
          </button>
        )}
        <div className="flex gap-3">
          <button data-testid="summary-timeline-btn" onClick={onTimeline} className="flex-1 glass rounded-sm py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-white/50 transition-colors">
            <Clock className="w-4 h-4" strokeWidth={1.5} /> Case files
          </button>
          <button data-testid="summary-new-btn" onClick={onNew} className="flex-1 glass rounded-sm py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-white/50 transition-colors">
            <Camera className="w-4 h-4" strokeWidth={1.5} /> New snap
          </button>
        </div>
      </div>
    </div>
  );
}
